import React, { useState, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Drawer,
  IconButton,
  TextField,
  TablePagination,
  Checkbox,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import BlockIcon from '@mui/icons-material/Block';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { MenuProduct, MenuState, MenuBundle, MenuVariant } from './types';
import { countExcludedWeights, countExcludedWeightsInSlot, isWeightExcluded } from './exclusions';


/** Rollup badge for a collapsed slot or modifier, so exclusions are findable without expanding the tree. */
const ExcludedCountChip: React.FC<{ count: number }> = ({ count }) => (
  <Tooltip title={`${count} weight${count === 1 ? '' : 's'} excluded from this variant`}>
    <Chip label={`${count} excluded`} color='error' variant='outlined' size='small' sx={{ ml: 1 }} />
  </Tooltip>
);


// Type guard
function isProduct(item: any): item is MenuProduct {
  return !!item && 'productCode' in item;
}


// Add onIssuesFilter prop for global issues filter
interface MenuInfoProps {
  menu: MenuState;
  optionValueCodeToName: Record<string, string>;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const MenuInfo: React.FC<MenuInfoProps> = ({ menu, optionValueCodeToName, expanded: externalExpanded, onExpandedChange }) => {
  const [apiError, setApiError] = useState<any>(null);
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  // Use external expanded state if provided, otherwise use internal
  const expanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;
  const setExpanded = onExpandedChange || setInternalExpanded;

  // Validate menuData
  React.useEffect(() => {
    if (!menu.product || !Array.isArray(menu.product) || !Array.isArray(menu.bundle)) {
      setApiError('Menu data is missing or malformed.');
    } else {
      setApiError(null);
    }
  }, [menu]);

  // State (all hooks must be at the top, before any return)
  const [menuState, setMenuState] = useState<MenuState>({
    ...menu,
    product: menu.product || [],
    bundle: menu.bundle || [],
    categories: menu.categories || [],
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuProduct | MenuBundle | null>(null);
  const [isEditing, setIsEditing] = useState<boolean[]>([]);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false); // default to show all
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [variantSearch, setVariantSearch] = useState('');

  const combinedList = React.useMemo(() => {
    const products = menuState.product.map((p) => ({ ...p, itemType: 'Product' as const }));
    const bundles = menuState.bundle.map((b) => ({ ...b, itemType: 'Bundle' as const }));
    return [...products, ...bundles].sort((a, b) => {
      if (a.itemType === 'Product' && b.itemType === 'Bundle') return -1;
      if (a.itemType === 'Bundle' && b.itemType === 'Product') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [menuState.product, menuState.bundle]);


  // Error counts
  const wrongProductPrice = React.useMemo(
    () => menuState.product.filter((p) => JSON.stringify(p).includes('9999')),
    [menuState.product]
  );
  const wrongBundlePrice = React.useMemo(() => menuState.bundle.filter((b) => b.price?.amount === 9999), [menuState.bundle]);
  const hasErrors = wrongProductPrice.length > 0 || wrongBundlePrice.length > 0;

  const formatPrice = React.useCallback((price?: { amount?: number }) => {
    if (price?.amount == null) return 'N/A';
    return `$${(price.amount / 100).toFixed(2)}`;
  }, []);
  // Remove category cross-referencing; only show category if present in menu data
  const updatePrice = React.useCallback(
    (variant: MenuVariant, newAmt: number) => {
      if (!selectedItem || !isProduct(selectedItem)) return;
      setMenuState((prev) => ({
        ...prev,
        product: prev.product.map((p) =>
          p.productCode === selectedItem.productCode
            ? {
                ...p,
                variants: p.variants.map((v) =>
                  v.variantCode === variant.variantCode
                    ? {
                        ...v,
                        price: {
                          amount: newAmt,
                          currencyCode: v.price?.currencyCode || 'USD',
                        },
                      }
                    : v
                ),
              }
            : p
        ),
      }));
    },
    [selectedItem]
  );
  const hasPriceError = React.useCallback((variant: MenuVariant) => {
    if (variant.price?.amount === 9999) return true;
    return !!variant.slots?.some(
      (slot) =>
        (slot.weightPriceRanges?.some((r) => r.price?.amount === 9999) ?? false) ||
        (slot.modifiers?.some((m) => m.weights?.some((w) => w.price?.amount === 9999) ?? false) ?? false)
    );
  }, []);
  const getVariantName = useCallback((variant: MenuVariant | MenuProduct) => {
    // Use only menu's selectedOptionValues (array of optionValueCodes)
    const menuOptions = ('selectedOptionValues' in variant && variant.selectedOptionValues) || [];
    let variantName = '';
    if (menuOptions.length > 0) {
      variantName = menuOptions
        .map((opt: any) => {
          // Try optionValueCode, fallback to optionValue?.optionValueCode, fallback to internalName
          const code = opt.optionValueCode || opt.optionValue?.optionValueCode;
          return code ? (optionValueCodeToName[code] || code) : (opt.optionValue?.internalName || '');
        })
        .filter(Boolean)
        .join(', ');
    }
    if (!variantName) {
      variantName = ('variantCode' in variant && variant.variantCode) || ('defaultVariantCode' in variant && (variant as any).defaultVariantCode) || 'Unnamed Variant';
    }
    return variantName;
  }, [optionValueCodeToName]);

  // Render a single variant tree node (for products with variants)
  const renderVariantTree = useCallback(
    (variant: MenuVariant, idx: number) => {
      if (showOnlyErrors && !hasPriceError(variant)) return null;
      const variantName = getVariantName(variant);

      const isErr = variant.price?.amount === 9999;
      const priceEl = isEditing[idx] ? (
        <TextField
          defaultValue={variant.price?.amount ?? ''}
          type='number'
          variant='standard'
          InputProps={{ inputProps: { min: 0 } }}
          onChange={(e) => updatePrice(variant, parseInt(e.target.value, 10))}
          sx={{
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
          }}
        />
      ) : (
        <Typography sx={isErr ? { color: 'red' } : {}}>{formatPrice(variant.price)}</Typography>
      );

      return (
        <TreeItem
          key={variant.variantCode}
          nodeId={variant.variantCode}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, width: '100%' }}>
              <Box>
                <Typography sx={hasPriceError(variant) ? { color: 'red' } : {}}>
                  {variantName}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem' }}>
                  {variant.variantCode}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              {priceEl}
            </Box>
          }
        >
          {variant.slots?.map((slot, si) => {
            const slotErr =
              (slot.weightPriceRanges?.some((r) => r.price?.amount === 9999) ?? false) ||
              (slot.modifiers?.some((m) => m.weights?.some((w) => w.price?.amount === 9999)) ?? false);
            const slotExcluded = countExcludedWeightsInSlot(slot);
            return (
              <TreeItem
                key={`${variant.variantCode}-slot-${si}`}
                nodeId={`${variant.variantCode}-slot-${si}`}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={slotErr ? { color: 'red' } : {}}>Slot: {slot.slotCode}</Typography>
                    {slotExcluded > 0 && <ExcludedCountChip count={slotExcluded} />}
                  </Box>
                }
              >
                {slot.weightPriceRanges?.map((r, ri) => (
                  <TreeItem
                    key={ri}
                    nodeId={`${variant.variantCode}-slot-${si}-range-${ri}`}
                    label={
                      <Typography sx={r.price?.amount === 9999 ? { color: 'red' } : {}}>
                        Range {r.rangeStart} to {r.rangeEnd ?? '∞'}: {formatPrice(r.price)}
                      </Typography>
                    }
                  />
                ))}
                {slot.modifiers?.map((mod, mi) => {
                  const modErr = mod.weights?.some((w) => w.price?.amount === 9999) ?? false;
                  const modExcluded = countExcludedWeights(mod);
                  return (
                    <TreeItem
                      key={mi}
                      nodeId={`${variant.variantCode}-slot-${si}-mod-${mi}`}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={modErr ? { color: 'red' } : {}}>Modifier: {mod.modifierCode}</Typography>
                          {modExcluded > 0 && <ExcludedCountChip count={modExcluded} />}
                        </Box>
                      }
                    >
                      {mod.weights?.map((w, wi) => {
                        const excluded = isWeightExcluded(w);
                        return (
                          <TreeItem
                            key={wi}
                            nodeId={`${variant.variantCode}-slot-${si}-mod-${mi}-weight-${wi}`}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={w.price?.amount === 9999 ? { color: 'red' } : {}}>
                                  {/* Only the weight itself is struck through - the price is still a fact about it. */}
                                  <Box component='span' sx={excluded ? { textDecoration: 'line-through' } : {}}>
                                    Weight: {w.modifierWeightCode}
                                  </Box>
                                  {' - '}
                                  {formatPrice(w.price)}
                                </Typography>
                                {excluded && (
                                  <Tooltip title='Configured on this modifier but suppressed for this variant (isExcludedFromVariant)'>
                                    <Chip
                                      icon={<BlockIcon />}
                                      label='Excluded from variant'
                                      color='error'
                                      variant='outlined'
                                      size='small'
                                      sx={{ ml: 1 }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                          />
                        );
                      })}
                    </TreeItem>
                  );
                })}
              </TreeItem>
            );
          })}
        </TreeItem>
      );
    },
    [hasPriceError, showOnlyErrors, updatePrice, isEditing, formatPrice, getVariantName]
  );

  // Render a single variant node for products with no variants, using defaultVariantCode
  const renderDefaultVariant = (product: any) => {
  if (!product.defaultVariantCode) return null;
    const variantName = getVariantName(product);
    let variantCode = product.defaultVariantCode;
    
    return (
      <TreeItem
        key={variantCode}
        nodeId={variantCode}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, width: '100%' }}>
            <Box>
              <Typography>{variantName}</Typography>
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem' }}>
                {variantCode}
              </Typography>
            </Box>
          </Box>
        }
      />
    );
  };
  // No local filtering; all filtering is now global in MenuViewer

  // Handlers
  const handlePageChange = useCallback((_: any, np: number) => setPage(np), []);
  const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);
  const openDrawer = useCallback((item: MenuProduct | MenuBundle) => {
    setSelectedItem(item);
    setDrawerOpen(true);
    setIsEditing(isProduct(item) ? item.variants.map(() => false) : []);
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedItem(null);
    setIsEditing([]);
  }, []);
  const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodes(nodeIds);
  };
  const getAllNodeIds = useCallback((variant: MenuVariant) => {
    const nodeIds: string[] = [variant.variantCode];

    variant.slots?.forEach((slot, si) => {
      const slotId = `${variant.variantCode}-slot-${si}`;
      nodeIds.push(slotId);

      slot.weightPriceRanges?.forEach((_, ri) => {
        nodeIds.push(`${variant.variantCode}-slot-${si}-range-${ri}`);
      });

      if (Array.isArray(slot.modifiers)) {
        slot.modifiers.forEach((modifier, mi) => {
          const modId = `${variant.variantCode}-slot-${si}-mod-${mi}`;
          nodeIds.push(modId);
          if (modifier.weights) {
            modifier.weights.forEach((_, wi) => {
              nodeIds.push(`${variant.variantCode}-slot-${si}-mod-${mi}-weight-${wi}`);
            });
          }
        });
      }
    });

    return nodeIds;
  }, []);
  const handleToggleShowErrors = useCallback(() => {
    setShowOnlyErrors((prev) => {
      const newShowOnlyErrors = !prev;

      if (selectedItem && isProduct(selectedItem)) {
        // Only expand everything if we're going from errors to all AND nothing is currently expanded
        if (!newShowOnlyErrors && expandedNodes.length === 0) {
          const allNodeIds = selectedItem.variants.flatMap(getAllNodeIds);
          setExpandedNodes(allNodeIds);
        } else if (newShowOnlyErrors) {
          // When switching to show only errors, keep current expansions
          const errorVariants = selectedItem.variants.filter(hasPriceError);
          const errorNodeIds = errorVariants.flatMap(getAllNodeIds);
          // Only keep expanded nodes that are part of variants with errors
          setExpandedNodes((prev) =>
            prev.filter((nodeId) => errorNodeIds.some((errorId: string) => nodeId.startsWith(errorId.split('-')[0])))
          );
        }
      }

      return newShowOnlyErrors;
    });
  }, [selectedItem, getAllNodeIds, expandedNodes, hasPriceError]);

  return (
    <>
      {apiError && (
        <Box sx={{ color: 'red', mb: 2 }}>
          <Typography color="error">Error loading products: {String(apiError.message || apiError)}</Typography>
        </Box>
      )}
      {/* Main accordion */}
      <Accordion expanded={expanded} onChange={(_e, val) => setExpanded(val)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={hasErrors ? { color: 'red' } : {}}>{menu.storeNumber}</Typography>
            {hasErrors && (
              <Tooltip
                title={`${wrongProductPrice.length} products and ${wrongBundlePrice.length} bundles with issues`}
              >
                <Chip icon={<InfoIcon />} label='Pricing Issues' color='error' size='small' />
              </Tooltip>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Error filter buttons */}
          {hasErrors && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              {wrongProductPrice.length > 0 && (
                <Chip
                  icon={<InfoIcon />}
                  label={`Products with Issues: ${wrongProductPrice.length}`}
                  color='error'
                  variant='outlined'
                  sx={{ fontWeight: 600 }}
                />
              )}
              {wrongBundlePrice.length > 0 && (
                <Chip
                  icon={<InfoIcon />}
                  label={`Bundles with Issues: ${wrongBundlePrice.length}`}
                  color='error'
                  variant='outlined'
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          )}

          {/* Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Render products */}
                {combinedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, idx) => {
                  if (item.itemType === 'Product') {
                    const product = item as MenuProduct;
                    // Show price summary for product (min-max of variant prices)
                    let priceSummary = 'N/A';
                    if (product.variants && product.variants.length > 0) {
                      const prices = product.variants.map((v: any) => v.price?.amount).filter((a: any) => typeof a === 'number');
                      if (prices.length > 0) {
                        const min = Math.min(...prices);
                        const max = Math.max(...prices);
                        priceSummary = min === max ? `$${(min / 100).toFixed(2)}` : `$${(min / 100).toFixed(2)} - $${(max / 100).toFixed(2)}`;
                      }
                    }
                    return (
                      <TableRow
                        key={`Product-${idx}`}
                        sx={{
                          backgroundColor: JSON.stringify(product).includes('9999') ? 'rgba(255,0,0,0.1)' : 'inherit',
                        }}
                      >
                        <TableCell>Product</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.productCode}</TableCell>
                        <TableCell>{priceSummary}</TableCell>
                        <TableCell>
                          <IconButton size='small' onClick={() => openDrawer(product)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  } else {
                    const bundle = item as MenuBundle;
                    return (
                      <TableRow
                        key={`Bundle-${idx}`}
                        sx={{
                          backgroundColor: JSON.stringify(bundle).includes('9999') ? 'rgba(255,0,0,0.1)' : 'inherit',
                        }}
                      >
                        <TableCell>Bundle</TableCell>
                        <TableCell>{bundle.name}</TableCell>
                        <TableCell>{bundle.bundleCode}</TableCell>
                        <TableCell>{formatPrice(bundle.price)}</TableCell>
                        <TableCell />
                      </TableRow>
                    )
                  }
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component='div'
              count={combinedList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Drawer for variant details */}
      <Drawer anchor='right' open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: 600, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h5'>
              {selectedItem?.name}{' '}
              {selectedItem &&
                `(${isProduct(selectedItem) ? selectedItem.productCode : (selectedItem as MenuBundle).bundleCode})`}
            </Typography>
            <IconButton onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Checkbox checked={showOnlyErrors} onChange={handleToggleShowErrors} color='error' />
            <Typography>Show Only Errors</Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* Expand/Collapse All buttons */}
            {selectedItem && isProduct(selectedItem) && (
              <>
                <IconButton
                  size="small"
                  onClick={() => {
                    // Expand all
                    const allNodeIds = selectedItem.variants && selectedItem.variants.length > 0
                      ? selectedItem.variants.flatMap(getAllNodeIds)
                      : [];
                    setExpandedNodes(allNodeIds);
                  }}
                  title="Expand All"
                >
                  <ExpandMoreIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setExpandedNodes([])}
                  title="Collapse All"
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search variants..."
            value={variantSearch}
            onChange={(e) => setVariantSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            {selectedItem && isProduct(selectedItem) && (
              <Box
                sx={{
                  mt: 2,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: 1,
                  minHeight: 200,
                  p: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <TreeView
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  expanded={expandedNodes}
                  onNodeToggle={handleNodeToggle}
                  sx={{
                    height: 'auto',
                    minHeight: 300,
                    maxHeight: 600,
                    flexGrow: 1,
                    maxWidth: '100%',
                    overflowY: 'auto',
                  }}
                >
                  {/* If variants exist, render them; otherwise, render defaultVariantCode */}
                  {selectedItem.variants && selectedItem.variants.length > 0
                    ? selectedItem.variants
                      .filter(v => getVariantName(v).toLowerCase().includes(variantSearch.toLowerCase()))
                      .sort((a, b) => getVariantName(a).localeCompare(getVariantName(b)))
                      .map((v, i) => renderVariantTree(v, i))
                    : renderDefaultVariant(selectedItem)}
                </TreeView>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default MenuInfo;
